import {createSelector, createSlice} from "@reduxjs/toolkit";

const initialState = {
    semiFinishedSemiFinishedForModalArray: [],
    semiFinishedSemiFinishedWeight: 0,
    semiFinishedSemiFinishedWeightNetto: 0,
    semiFinishedSemiFinishedCost: 0,
    semiFinishedSemiFinishedEnergy: 0,
    semiFinishedSemiFinishedFats: 0,
    semiFinishedSemiFinishedProteins: 0,
    semiFinishedSemiFinishedCarbohydrates: 0,
    trigger: 0,
};

const semiFinishedSemiFinishedSlice = createSlice({
    name: "semiFinishedSemiFinishedSlice",
    initialState,
    reducers: {
        changeTrigger: (state) => {
            state.trigger += 1;
        },
        updateArray: (state, action) => {
                // Process the array to ensure weightNetto is calculated for display
            state.semiFinishedSemiFinishedForModalArray = action.payload.map(item => {
                // If weightNetto is already defined, use it
                if (item.weightNetto !== undefined && item.weightNetto !== null) {
                    return item;
                }
                
                // Calculate weightNetto based on child's ratio
                if (item.child) {
                    const weight = parseFloat(item.weight) || 0;
                    const childWeight = parseFloat(item.child.weight) || 0;
                    const childWeightNetto = parseFloat(item.child.weightNetto) || 0;
                    const nettoRatio = childWeight > 0 ? childWeightNetto / childWeight : 1;
                    
                    return {
                        ...item,
                        weightNetto: Number((weight * nettoRatio).toFixed(2))
                    };
                }
                
                // Fallback - if no child data, assume weightNetto equals weight
                return {
                    ...item,
                    weightNetto: item.weight
                };
            });
            
            semiFinishedSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        addSemiFinishedForModal: (state, action) => {
            state.semiFinishedSemiFinishedForModalArray.push(action.payload);
            semiFinishedSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        updateSemiFinishedSemiFinishedForModal: (state, action) => {
            state.semiFinishedSemiFinishedForModalArray = action.payload;
            semiFinishedSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        removeSemiFinishedForModal: (state, action) => {
            state.semiFinishedSemiFinishedForModalArray = state.semiFinishedSemiFinishedForModalArray.filter(
                (product) => product.id !== action.payload
            );
            semiFinishedSemiFinishedSlice.caseReducers.recalculateTotals(state);
        },
        clearArray: (state) => {
            state.semiFinishedSemiFinishedForModalArray = [];
            state.semiFinishedSemiFinishedWeight = 0;
            state.semiFinishedSemiFinishedWeightNetto = 0;
            state.semiFinishedSemiFinishedCost = 0;
            state.semiFinishedSemiFinishedEnergy = 0;
            state.semiFinishedSemiFinishedFats = 0;
            state.semiFinishedSemiFinishedProteins = 0;
            state.semiFinishedSemiFinishedCarbohydrates = 0;
        },
        recalculateTotals: (state) => {
            const array = state.semiFinishedSemiFinishedForModalArray;

            state.semiFinishedSemiFinishedWeight = array.reduce(
                (sum, product) => sum + (parseFloat(product.weight) || 0), 0
            );

            state.semiFinishedSemiFinishedWeightNetto = array.reduce(
                (sum, product) => {
                    const weight = parseFloat(product.weight) || 0;
                    
                    // Check if this is a manually added item with pre-calculated weightNetto
                    if (product.weightNetto !== undefined) {
                        return sum + (parseFloat(product.weightNetto) || 0);
                    }
                    
                    // Handle API data structure - calculate weightNetto based on child's ratio
                    if (product.child) {
                        const childWeight = parseFloat(product.child.weight) || 0;
                        const childWeightNetto = parseFloat(product.child.weightNetto) || 0;
                        const nettoRatio = childWeight > 0 ? childWeightNetto / childWeight : 0;
                        return sum + (weight * nettoRatio);
                    }
                    
                    // Fallback - assume 100% ratio if no ratio data available
                    return sum + weight;
                }, 0
            );

            state.semiFinishedSemiFinishedCost = parseFloat(
                array.reduce((sum, product) => sum + (parseFloat(product.cost) || 0), 0).toFixed(2)
            );

            state.semiFinishedSemiFinishedEnergy = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.energy !== undefined) {
                        return sum + (parseFloat(product.energy) || 0);
                    }
                    
                    // Handle API data structure - child values are already calculated with cooking ratio applied
                    const energy = parseFloat(product.child?.energy) || 0;
                    return sum + energy;
                }, 0).toFixed(2)
            );

            state.semiFinishedSemiFinishedFats = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.fats !== undefined) {
                        return sum + (parseFloat(product.fats) || 0);
                    }
                    
                    // Handle API data structure - child values are already calculated with cooking ratio applied
                    const fats = parseFloat(product.child?.fats) || 0;
                    return sum + fats;
                }, 0).toFixed(2)
            );

            state.semiFinishedSemiFinishedProteins = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.proteins !== undefined) {
                        return sum + (parseFloat(product.proteins) || 0);
                    }
                    
                    // Handle API data structure - child values are already calculated with cooking ratio applied
                    const proteins = parseFloat(product.child?.proteins) || 0;
                    return sum + proteins;
                }, 0).toFixed(2)
            );

            state.semiFinishedSemiFinishedCarbohydrates = parseFloat(
                array.reduce((sum, product) => {
                    // Check if this is pre-calculated (manually added item)
                    if (product.carbohydrates !== undefined) {
                        return sum + (parseFloat(product.carbohydrates) || 0);
                    }
                    
                    // Handle API data structure - child values are already calculated with cooking ratio applied
                    const carbs = parseFloat(product.child?.carbs) || 0;
                    return sum + carbs;
                }, 0).toFixed(2)
            );
        },
    },
});

const arrayFormattedSemiFinished = createSelector(
    (state) => state.semiFinishedSemiFinished.semiFinishedSemiFinishedForModalArray,
    (array) =>
        array.map((product, index) => ({
            index: index,
            childId: product.semiFinishedId ?? product.childId ?? product.id,
            weight: product.weight,
            weightNetto: product.weightNetto,
            cost: product.cost,
            price: product.price,
        }))
);

const {reducer: semiFinishedSemiFinishedReducer, actions} = semiFinishedSemiFinishedSlice;
const semiFinishedSemiFinishedActions = {...actions};

export {
    semiFinishedSemiFinishedReducer,
    semiFinishedSemiFinishedActions,
    arrayFormattedSemiFinished,
};

